import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export type User = {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
  created_at: string;
  organization?: string | null;
  website_url?: string | null;
  is_active: boolean;
  email_confirmed?: boolean;
  plan?: string;
};

export const useUsersList = (refreshTrigger: number = 0) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('Fetching ALL users, including auth users...');
      
      // Appeler la fonction Edge pour récupérer tous les utilisateurs auth
      const { data: authUsers, error: authError } = await supabase.functions.invoke('get-all-users');
      
      if (authError) {
        console.error('Error fetching auth users:', authError);
        throw authError;
      }
      
      console.log('Retrieved auth users:', authUsers?.length || 0);
      
      // Récupérer les profils
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        throw profilesError;
      }
      
      console.log('Retrieved profiles:', profiles?.length || 0);
      
      // Fusionner les données des deux sources
      const mergedUsers = createMergedUsersList(authUsers || [], profiles || []);
      console.log('Merged users data:', mergedUsers.length, JSON.stringify(mergedUsers, null, 2));
      
      setUsers(mergedUsers);
    } catch (error: any) {
      console.error('Error in fetchUsers:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de charger les utilisateurs.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour fusionner les utilisateurs auth avec les profils
  const createMergedUsersList = (authUsers: any[], profiles: any[]): User[] => {
    // Créer une map des profils pour recherche rapide
    const profilesMap = new Map();
    profiles.forEach(profile => {
      profilesMap.set(profile.id, profile);
    });
    
    // Fusionner les données
    return authUsers.map(authUser => {
      const profile = profilesMap.get(authUser.id) || {};
      
      return {
        id: authUser.id,
        email: authUser.email,
        full_name: profile.full_name || authUser.user_metadata?.full_name || '',
        role: profile.role || authUser.user_metadata?.role || 'candidat',
        created_at: profile.created_at || authUser.created_at,
        organization: profile.organization || authUser.user_metadata?.organization || '',
        website_url: profile.website_url || authUser.user_metadata?.website_url || '',
        is_active: profile.is_active !== undefined ? profile.is_active : true,
        email_confirmed: authUser.email_confirmed || false,
        plan: profile.plan || 'free'
      };
    });
  };

  // This function ensures all auth users have corresponding profiles
  const syncAllUsers = async () => {
    try {
      console.log('Starting sync_users_and_profiles...');
      const { error } = await supabase.rpc('sync_users_and_profiles');
      
      if (error) {
        console.error('Error syncing users with profiles:', error);
        throw error;
      }
      
      console.log('Successfully completed sync_users_and_profiles');
    } catch (error: any) {
      console.error('Failed to sync users:', error);
      // We'll continue anyway to show existing users
    }
  };

  const setAdminRole = async (email: string) => {
    try {
      const { error } = await supabase.rpc('set_admin_role', { email_address: email });
      
      if (error) throw error;
      
      toast({
        title: "Succès",
        description: `Le rôle administrateur a été attribué à ${email}.`,
      });
      
      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de mettre à jour le rôle utilisateur.",
        variant: "destructive"
      });
    }
  };

  const toggleUserActive = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase.rpc('toggle_user_active_status', { 
        user_id: userId,
        new_status: isActive
      });
      
      if (error) throw error;
      
      toast({
        title: "Succès",
        description: `Le compte a été ${isActive ? 'activé' : 'désactivé'} avec succès.`,
      });
      
      fetchUsers();
    } catch (error: any) {
      console.error("Erreur de changement de statut:", error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de modifier le statut de l'utilisateur.",
        variant: "destructive"
      });
    }
  };

  const deleteUser = async (userId: string, email: string) => {
    try {
      setIsDeleting(true);
      
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { userId: userId }
      });
      
      if (error) throw error;
      
      toast({
        title: "Utilisateur supprimé",
        description: `L'utilisateur ${email} a été supprimé avec succès.`,
      });
      
      fetchUsers();
    } catch (error: any) {
      console.error("Erreur détaillée:", error);
      toast({
        title: "Erreur de suppression",
        description: error.message || "Impossible de supprimer l'utilisateur. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    users,
    loading,
    isDeleting,
    fetchUsers,
    syncAllUsers,
    setAdminRole,
    toggleUserActive,
    deleteUser
  };
};
